// ─── src/GewPartnerApplicationsView.tsx ────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  ExternalLink,
  MapPin,
  Search,
  XCircle,
} from "lucide-react";
import {
  getGewPartnerApplication,
  listGewPartnerApplications,
  type ApplicationStatus,
  type GewPartnerApplicationDetail,
  type GewPartnerApplicationSummary,
} from "./api/gewPartnerApplications";
import { Pagination } from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";

const LIMIT = 20;

const STATUS_LABEL: Record<ApplicationStatus, string> = {
  draft: "Draft",
  under_review: "Under review",
  info_required: "Info required",
  approved: "Approved",
  waitlist: "Waitlist",
  rejected: "Rejected",
  suspended: "Suspended",
};

/**
 * Dark-mode-ready status pills. Keeps colour meaning (amber = waiting,
 * emerald = approved, rose = rejected) but at the opacity + text
 * contrast the rest of sws-dashboard uses so the view no longer reads
 * as a bright light-mode island inside the dark shell.
 */
const STATUS_COLOR: Record<ApplicationStatus, string> = {
  draft: "bg-brand-border/40 text-brand-muted border border-brand-border",
  under_review: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  info_required: "bg-orange-500/15 text-orange-300 border border-orange-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  waitlist: "bg-sky-500/15 text-sky-300 border border-sky-500/30",
  rejected: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
  suspended: "bg-brand-border/40 text-brand-muted border border-brand-border",
};

const STATUS_FILTERS: Array<"" | ApplicationStatus> = [
  "",
  "under_review",
  "info_required",
  "approved",
  "waitlist",
  "rejected",
];

function exportCsv(rows: GewPartnerApplicationSummary[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = [
    "Org Name (AR)",
    "Org Name (EN)",
    "Type",
    "Governorate",
    "City",
    "Primary Contact",
    "Email",
    "Phone",
    "Status",
    "Submitted At",
  ].join(",");
  const body = rows.map((r) => {
    const primary = r.primaryContact;
    return [
      r.organization?.nameAr ?? "",
      r.organization?.nameEn ?? "",
      r.organization?.type ?? "",
      r.organization?.primaryGovernorate ?? "",
      r.organization?.primaryCity ?? "",
      primary?.fullName ?? "",
      primary?.email ?? "",
      primary?.phone ?? "",
      r.status,
      r.createdAt,
    ]
      .map(esc)
      .join(",");
  });
  const blob = new Blob(["﻿" + [header, ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `gew_partner_applications_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function GewPartnerApplicationsView() {
  const [status, setStatus] = useState<"" | ApplicationStatus>("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);

  const { data = [], isLoading, isFetching, isError } = useQuery({
    queryKey: ["gew-partner-applications", status],
    queryFn: () => listGewPartnerApplications(status || undefined),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((r) => {
      const bag = [
        r.organization?.nameAr ?? "",
        r.organization?.nameEn ?? "",
        r.organization?.primaryCity ?? "",
        r.organization?.primaryGovernorate ?? "",
        r.primaryContact
          ? `${r.primaryContact.fullName} ${r.primaryContact.email}`
          : "",
        r.backupContact
          ? `${r.backupContact.fullName} ${r.backupContact.email}`
          : "",
      ]
        .join(" ")
        .toLowerCase();
      return bag.includes(q);
    });
  }, [data, search]);

  // Client-side pagination — the list endpoint returns all rows at
  // once (there are at most a few hundred), so slicing in-memory is
  // the simplest path to a paginated table.
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const paginated = useMemo(
    () => filtered.slice((page - 1) * LIMIT, page * LIMIT),
    [filtered, page],
  );

  // Reset to page 1 whenever filters change — otherwise a 3rd-page
  // state can look empty after narrowing to under 40 rows.
  useEffect(() => {
    setPage(1);
  }, [status, search]);

  // Guard: if a delete / status change shrinks total below the
  // current page's start, nudge back to the last valid page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const stats = useMemo(() => {
    const by: Record<string, number> = {};
    for (const r of data) by[r.status] = (by[r.status] ?? 0) + 1;
    return { total: data.length, byStatus: by };
  }, [data]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Partner Applications</h1>
        <p className="mt-1 text-sm text-brand-muted">
          Submissions from the new partner-portal application flow
          (startupsyria.org/gew/2026/join). Approve / reject actions live
          in the partners portal — this view is read-only.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatPill label="Total" value={stats.total} />
        <StatPill
          label="Under review"
          value={stats.byStatus.under_review ?? 0}
          tone="amber"
        />
        <StatPill
          label="Approved"
          value={stats.byStatus.approved ?? 0}
          tone="emerald"
        />
        <StatPill
          label="Rejected"
          value={stats.byStatus.rejected ?? 0}
          tone="rose"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by org, contact, email, city…"
            className="w-full rounded-lg border border-brand-border bg-brand-navy pl-9 pr-3 py-2 text-sm text-white placeholder-brand-muted outline-none focus:border-brand-green"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as "" | ApplicationStatus)}
          className="rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-sm text-white outline-none focus:border-brand-green"
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s} className="bg-brand-navy">
              {s ? STATUS_LABEL[s] : "All statuses"}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => exportCsv(filtered)}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-sm font-semibold text-brand-muted hover:text-white hover:border-brand-orange transition-colors disabled:opacity-50"
        >
          <Download size={13} /> CSV
        </button>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading && <TableSkeleton />}
        {isError && (
          <p className="p-6 text-sm text-rose-400">Failed to load applications.</p>
        )}
        {!isLoading && !isError && filtered.length === 0 && (
          <p className="p-6 text-sm text-brand-muted">No applications yet.</p>
        )}
        {!isLoading && !isError && filtered.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-border text-left text-xs uppercase text-brand-muted">
                <th className="px-3 py-3 w-8"></th>
                <th className="px-3 py-3">Organisation</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Location</th>
                <th className="px-3 py-3">Primary contact</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((r) => {
                const primary = r.primaryContact;
                const org = r.organization;
                const open = expanded.has(r.id);
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors cursor-pointer"
                      onClick={() => toggle(r.id)}
                    >
                      <td className="px-3 py-3 text-brand-muted">
                        {open ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          {org?.logoUrl ? (
                            <img
                              src={org.logoUrl}
                              alt=""
                              className="h-7 w-7 rounded border border-brand-border bg-white object-contain"
                            />
                          ) : (
                            <div className="grid h-7 w-7 place-items-center rounded border border-brand-border bg-brand-navy text-brand-muted">
                              <Building2 size={13} />
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-white">
                              {org?.nameAr ?? "—"}
                            </p>
                            {org?.nameEn && (
                              <p className="text-xs text-brand-muted" dir="ltr">
                                {org.nameEn}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 capitalize text-brand-muted">
                        {org?.type ?? "—"}
                      </td>
                      <td className="px-3 py-3 text-brand-muted">
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={12} className="text-brand-muted" />
                          {[org?.primaryCity, org?.primaryGovernorate]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        {primary ? (
                          <>
                            <p className="font-medium text-white">
                              {primary.fullName}
                            </p>
                            <p className="text-xs text-brand-muted" dir="ltr">
                              {primary.email}
                            </p>
                          </>
                        ) : (
                          <span className="text-brand-muted">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${STATUS_COLOR[r.status]}`}
                        >
                          {STATUS_LABEL[r.status]}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-brand-muted">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-brand-border/10">
                        <td colSpan={7} className="px-6 py-5">
                          <ApplicationDetails id={r.id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && !isError && total > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={LIMIT}
          onPageChange={setPage}
          isFetching={isFetching}
        />
      )}
    </div>
  );
}

function ApplicationDetails({ id }: { id: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["gew-partner-application", id],
    queryFn: () => getGewPartnerApplication(id),
  });

  if (isLoading) {
    return <p className="text-xs text-brand-muted">Loading…</p>;
  }
  if (!data) {
    return <p className="text-xs text-rose-400">Failed to load details.</p>;
  }
  return <ApplicationDetailBlock app={data} />;
}

function ApplicationDetailBlock({ app }: { app: GewPartnerApplicationDetail }) {
  const org = app.organization;
  const contacts = [
    app.primaryContact
      ? { ...app.primaryContact, role: "primary" as const }
      : null,
    app.backupContact
      ? { ...app.backupContact, role: "backup" as const }
      : null,
  ].filter(Boolean) as Array<{
    role: "primary" | "backup";
    fullName: string;
    email: string;
    phone: string | null;
    title: string | null;
  }>;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <section>
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-brand-muted">
          Organisation
        </h3>
        {org ? (
          <dl className="space-y-1 text-xs">
            <Row label="Name (AR)" value={org.nameAr} />
            <Row label="Name (EN)" value={org.nameEn ?? "—"} />
            <Row label="Type" value={org.type} />
            <Row
              label="Location"
              value={
                [org.primaryCity, org.primaryGovernorate]
                  .filter(Boolean)
                  .join(" · ") || "—"
              }
            />
            <Row label="Org status" value={org.status} />
          </dl>
        ) : (
          <p className="text-xs text-brand-muted">
            Organisation record missing.
          </p>
        )}
      </section>
      <section>
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-brand-muted">
          Contacts
        </h3>
        {contacts.length === 0 ? (
          <p className="text-xs text-brand-muted">No contacts.</p>
        ) : (
          <ul className="space-y-2 text-xs">
            {contacts.map((c, i) => (
              <li
                key={i}
                className="rounded-lg border border-brand-border bg-brand-navy p-2.5"
              >
                <p className="font-semibold text-white">
                  {c.fullName}
                  <span className="ml-1.5 rounded bg-brand-border/40 px-1.5 py-0.5 text-[10px] uppercase text-brand-muted">
                    {c.role}
                  </span>
                </p>
                {c.title && (
                  <p className="mt-0.5 text-brand-muted">{c.title}</p>
                )}
                <p className="mt-0.5 text-brand-muted" dir="ltr">
                  {c.email}
                </p>
                {c.phone && (
                  <p className="text-brand-muted" dir="ltr">
                    {c.phone}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="md:col-span-2">
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-brand-muted">
          Reviews
        </h3>
        {app.reviews.length === 0 ? (
          <p className="text-xs text-brand-muted">
            No decision recorded yet — pending reviewer action in the portal.
          </p>
        ) : (
          <ul className="space-y-2 text-xs">
            {app.reviews.map((r) => (
              <li
                key={r.id}
                className="rounded-lg border border-brand-border bg-brand-navy p-2.5"
              >
                <p className="flex items-center gap-2 text-brand-muted">
                  <Clock size={11} />
                  <span dir="ltr">{new Date(r.createdAt).toLocaleString()}</span>
                  <span className="font-semibold text-white">
                    {r.decision === "approve" ? (
                      <CheckCircle2
                        size={11}
                        className="inline text-emerald-400"
                      />
                    ) : r.decision === "reject" ? (
                      <XCircle size={11} className="inline text-rose-400" />
                    ) : null}{" "}
                    {r.decision}
                  </span>
                  {r.reviewerEmail && (
                    <span className="text-brand-muted" dir="ltr">
                      · {r.reviewerEmail}
                    </span>
                  )}
                </p>
                {r.partnerFacingNote && (
                  <p className="mt-1 whitespace-pre-wrap text-gray-200">
                    <strong>Note to partner:</strong> {r.partnerFacingNote}
                  </p>
                )}
                {r.internalNote && (
                  <p className="mt-1 whitespace-pre-wrap text-brand-muted">
                    <strong>Internal:</strong> {r.internalNote}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      <p className="md:col-span-2 flex items-center gap-1.5 text-xs text-brand-muted">
        <ExternalLink size={11} />
        Approve/reject this application from the partners portal:
        <a
          href={`https://partners-dashboard.startupsyria.org/dashboard/admin/applications/${app.id}`}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-brand-orange underline"
        >
          open in portal
        </a>
      </p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-brand-muted">{label}</dt>
      <dd className="flex-1 text-gray-200">{value}</dd>
    </div>
  );
}

function StatPill({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "amber" | "emerald" | "rose";
}) {
  const toneClass =
    tone === "amber"
      ? "bg-amber-500/10 text-amber-200 border-amber-500/30"
      : tone === "emerald"
        ? "bg-emerald-500/10 text-emerald-200 border-emerald-500/30"
        : tone === "rose"
          ? "bg-rose-500/10 text-rose-200 border-rose-500/30"
          : "bg-brand-navy text-white border-brand-border";
  return (
    <div className={`rounded-xl border p-3 ${toneClass}`}>
      <p className="text-xs font-semibold uppercase tracking-wider opacity-70">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </div>
  );
}
